import { ErrorTypes, createErrorWithType } from './errorHandling';
import { TEST_CONFIG } from './testConstants';
import { QuestionFetcher } from './questions/questionFetcher';
import { QuestionProcessor } from './questions/questionProcessor';
import { ScoreCalculator } from './questions/questionProcessor';
import { FailedQuestionsManager, ProgressManager } from './questions/storageManager';
import { getBrowserFingerprint } from '../services/browserFingerprint';

// Public API
export const cargarTodasLasPreguntas = async (signal, token) => {
  try {
    // Get browser fingerprint for trial access
    const fingerprint = await getBrowserFingerprint();

    // Try to get trial access first if no token
    if (!token) {
      try {
        const trialResponse = await fetch('/api/trial/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Browser-Fingerprint': fingerprint
          }
        });

        if (trialResponse.ok) {
          const trialData = await trialResponse.json();
          if (trialData.success) {
            console.log('Trial access granted until:', trialData.trial.expiresAt);
            
            // Check if the user has tests available
            if (trialData.trial.testsAvailable === 0 || trialData.trial.ipLimited) {
              console.error('No tests available or IP limited:', {
                testsAvailable: trialData.trial.testsAvailable,
                ipLimited: trialData.trial.ipLimited,
                message: trialData.trial.message
              });
              
              throw createErrorWithType(
                ErrorTypes.AUTH,
                trialData.trial.message || 'No tienes acceso a más tests gratuitos hoy.'
              );
            }
          }
        }
      } catch (error) {
        if (error.type === ErrorTypes.AUTH) {
          throw error; // Re-throw authentication errors
        }
        console.error('Error getting trial access:', error);
      }
    }

    // Make request with fingerprint header (if available)
    // Only include fingerprint for non-authenticated users
    const fingerprintToSend = token ? null : (fingerprint || null);
    console.log('Using fingerprint in API request:', token ? 'No (authenticated user)' : 'Yes (trial user)');
    
    const response = await QuestionFetcher.fromApi('/api/questions/getAll', token, fingerprintToSend);
    const questions = response.tests;

    if (!questions || !questions.length) {
      throw createErrorWithType(ErrorTypes.DATA_NOT_FOUND, 'No se encontraron preguntas');
    }

    return questions;
  } catch (error) {
    throw createErrorWithType(ErrorTypes.DATA_LOADING, 'Error al cargar las preguntas: ' + error.message);
  }
};

export const cargarPreguntasPorTipo = async (tipo, signal, token, questionCount = null, randomSeed = null) => {
  if (!['viajeros', 'mercancias'].includes(tipo)) {
    throw createErrorWithType(ErrorTypes.INVALID_INPUT, 'Tipo de preguntas inválido');
  }

  try {
    console.log('Starting cargarPreguntasPorTipo:', { tipo, hasToken: !!token, questionCount });
    
    // If user has a token, skip trial access check completely
    if (!token) {
      // Only get fingerprint and check trial access if user is not authenticated
      const fingerprint = await getBrowserFingerprint();
      console.log('Got browser fingerprint:', fingerprint ? 'valid fingerprint' : 'no fingerprint');
      
      try {
        console.log('Requesting trial access...');
        
        // Only include fingerprint header if it's valid
        const headers = {
          'Content-Type': 'application/json',
          'credentials': 'same-origin'
        };
        
        if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 10) {
          headers['X-Browser-Fingerprint'] = fingerprint;
          console.log('Including fingerprint in trial request:', fingerprint.substring(0, 10) + '...');
        } else {
          console.log('No valid fingerprint for trial request:', fingerprint);
        }
        
        const trialResponse = await fetch('/api/trial/register', {
          method: 'POST',
          headers: headers,
          credentials: 'same-origin'
        });

        if (trialResponse.ok) {
          const trialData = await trialResponse.json();
          if (trialData.success) {
            console.log('Trial access granted until:', trialData.trial.expiresAt);
            
            // Check if the user has tests available
            if (trialData.trial.testsAvailable === 0 || trialData.trial.ipLimited) {
              console.error('No tests available or IP limited:', {
                testsAvailable: trialData.trial.testsAvailable,
                ipLimited: trialData.trial.ipLimited,
                message: trialData.trial.message
              });
              
              throw createErrorWithType(
                ErrorTypes.AUTH,
                trialData.trial.message || 'No tienes acceso a más tests gratuitos hoy.'
              );
            }
          }
        } else {
          console.error('Trial access request failed:', await trialResponse.text());
        }
      } catch (error) {
        if (error.type === ErrorTypes.AUTH) {
          throw error; // Re-throw authentication errors
        }
        console.error('Error getting trial access:', error);
      }
    } else {
      console.log('User is authenticated with token, skipping trial access check');
    }

    // Use provided questionCount or default to TEST_CONFIG.TOTAL_QUESTIONS
    const totalQuestions = questionCount || TEST_CONFIG.TOTAL_QUESTIONS;

    // Initialize fingerprint variable for authenticated users
    let fingerprint;
    if (!token) {
      // fingerprint is already defined for trial users
    } else {
      // For authenticated users, we don't need a fingerprint
      fingerprint = null;
    }

    console.log('Making API request for questions:', {
      tipo,
      totalQuestions,
      hasToken: !!token,
      hasFP: !!fingerprint,
      fingerprintLength: fingerprint ? fingerprint.length : 0,
      fingerprintType: fingerprint ? typeof fingerprint : 'undefined',
      randomSeed: randomSeed ? randomSeed.substring(0, 10) + '...' : 'not provided'
    });

    // Make request with fingerprint header and randomCount for test questions
    console.log('Requesting questions from API...');
    
    // Only include fingerprint for non-authenticated users
    const fingerprintToSend = token ? null : (fingerprint || null);
    console.log('Using fingerprint in API request:', token ? 'No (authenticated user)' : 'Yes (trial user)');
    
    // Add randomSeed to the URL if provided
    const randomSeedParam = randomSeed ? `&randomSeed=${randomSeed}` : '';
    
    const questions = await QuestionFetcher.fromApi(
      `/api/questions/getByType?type=${tipo}&randomCount=${totalQuestions}${randomSeedParam}`,
      token,
      fingerprintToSend
    );

    if (!questions || !questions.length) {
      console.error('No questions found:', {
        tipo,
        totalQuestions,
        questionCount: questions?.length
      });
      throw createErrorWithType(
        ErrorTypes.DATA_NOT_FOUND,
        'No se encontraron preguntas para el tipo especificado'
      );
    }

    // Group questions by module
    const moduleDistribution = {};
    questions.forEach(q => {
      const key = `${q.moduloId} - ${q.modulo}`;
      moduleDistribution[key] = (moduleDistribution[key] || 0) + 1;
    });

    console.log('Questions loaded by module:');
    Object.entries(moduleDistribution)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([module, count]) => {
        console.log(`${module}: ${count} questions`);
      });

    // Take exactly the requested number of questions since they're already randomized
    const selectedQuestions = questions.slice(0, totalQuestions);

    console.log('Selected questions:', {
      count: selectedQuestions.length,
      distribution: {
        common: selectedQuestions.filter(q => q.moduloId === 'comun').length,
        specific: selectedQuestions.filter(q => q.moduloId === tipo).length
      },
      first: selectedQuestions[0],
      last: selectedQuestions[selectedQuestions.length - 1]
    });

    return selectedQuestions;
  } catch (error) {
    // If it's already an AUTH error, pass it through
    if (error.type === ErrorTypes.AUTH) {
      throw error;
    }
    throw createErrorWithType(ErrorTypes.DATA_LOADING, 'Error al cargar las preguntas: ' + error.message);
  }
};

// Removed obtenerPreguntasAleatorias as randomization is now handled server-side

export const calcularPuntuacionExamen = (respuestas, preguntas) => {
  return ScoreCalculator.calculate(respuestas, preguntas);
};

// Export storage-related functions
export const {
  add: guardarPreguntaFallada,
  getAll: obtenerPreguntasFalladas,
  remove: eliminarPreguntaFallada
} = FailedQuestionsManager;

export const {
  updateModule: guardarProgresoModulo,
  getAll: obtenerProgreso
} = ProgressManager;
